import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Stack,
  TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import type { AcceptInviteResponse, InviteInfo } from '../api/types';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setLoadErr('Missing invitation token in URL.'); setLoading(false); return; }
    api.get<InviteInfo>(`/api/auth/invite-info`, { params: { token } })
      .then((r) => {
        setInfo(r.data);
        // Pre-fill the username from the email's local-part as a sensible default.
        setUsername(r.data.email.split('@')[0]);
      })
      .catch((ex) => setLoadErr(ex?.response?.data?.error ?? ex.message))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post<AcceptInviteResponse>('/api/auth/accept-invite', {
        Token: token, Username: username, Password: password,
      });
      if (data.autoLogin && data.token && data.user) {
        setSession(data.token, data.user);
        navigate('/home', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (ex: unknown) {
      setErr(((ex as { response?: { data?: { error?: string } } })?.response?.data?.error) ?? (ex as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="sm">
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>You're invited to Template.NET</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set a username and password to finish creating your account.
            </Typography>

            {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loading && loadErr && <Alert severity="error">{loadErr}</Alert>}
            {!loading && info && (
              <Stack component="form" spacing={2} onSubmit={onSubmit}>
                <TextField label="Email" value={info.email} disabled fullWidth size="small" />
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Roles you'll be granted
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {info.roles.map((r) => <Chip key={r} label={r} size="small" />)}
                  </Stack>
                </Box>
                <TextField
                  label="Username" value={username} required autoFocus size="small"
                  onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                  label="Password" type="password" value={password} required size="small"
                  onChange={(e) => setPassword(e.target.value)}
                  helperText="Minimum 8 characters"
                />
                <TextField
                  label="Confirm password" type="password" value={confirm} required size="small"
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {err && <Alert severity="error">{err}</Alert>}
                <Button type="submit" variant="contained" size="large" disabled={busy}
                        startIcon={busy ? <CircularProgress size={18} /> : null}>
                  {busy ? 'Creating account...' : 'Create account'}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
